import LoaderOverlay from '@allesauto/core/loaderOverlay';
import TextLink from '@allesauto/core/textLink';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { Routes_DE as Routes, TradeInFlowType } from '@allesauto/utils/consts';
import { isSpecialCharacter } from '@allesauto/utils/funcs';
import {
  ArrowsVertical16,
  Car16,
  Chat16,
  Location16,
  Pen16,
  Printer16,
} from '@carbon/icons-react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import ContactDealerForm from '@/components/forms/contactDealerForm/ContactDealerForm';
import TestDriveBookingForm from '@/components/forms/TestDriveBookingForm/TestDriveBookingForm';

import { useWhiteLabel } from '@/../../../packages/allesauto-core/src/lib/whiteLabel';
import { WL_INTEGRATION_WINTER_LAUSITZ } from '@/features/whitelabel/util/getWhiteLabelIntegrationSlugs';
import useToken from '@/global/providers/auth';
import { closeOverlay, openOverlay } from '@/store/redux/actions/app';

export interface CustomerDetails {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
}
export interface CarDealerDetailsProps {
  dealerDetails?: {
    contactPerson?: SchemaTypes.ContactPerson | null;
    location?: SchemaTypes.Dealer['location'];
    id?: string;
    name?: string | null;
  };
  vehicleSku: string;
  carModel: string;
  carManufacturer: string;
  customerDetails?: CustomerDetails;
  vehicleId: string;
  isOrderVehicle?: boolean | null;
  carConfigurator?: {
    isConfigurableCar: boolean;
    handleConfiguratorDealer: () => void;
    hasCarConfiguratorDealer: boolean;
    carConfiguratorId?: string;
  };
  isAccessory?: boolean;
  onSelectAnotherDealerClick?: () => void;
  vehiclePrice?: number;
  paymentType: SchemaTypes.PaymentType | null;
  financeCalculationResult?: SchemaTypes.FinanceCalculationResultInput;
  openConfigurator?: () => void;
}

const CarDealerDetails: FC<React.PropsWithChildren<CarDealerDetailsProps>> = ({
  dealerDetails,
  vehicleSku,
  carModel,
  carManufacturer,
  customerDetails,
  vehicleId,
  isOrderVehicle,
  carConfigurator,
  isAccessory = false,
  onSelectAnotherDealerClick,
  vehiclePrice = 0,
  paymentType,
  openConfigurator,
  financeCalculationResult,
}) => {
  const dispatch = useDispatch();
  const whiteLabel = useWhiteLabel();
  const { monthlyInstallment, downPayment, duration, mileage } = useSelector(
    (state: any) => state.pages.pdp.vehicle.financing || { type: null }
  );
  const {
    location = {
      city: '',
      houseNumber: '',
      street: '',
      zip: '',
      additionalAddress: '',
    },
    name = '',
    contactPerson = { email: '', firstName: '', lastName: '', phone: '' },
    id = '',
  } = dealerDetails ?? {};
  const { street, houseNumber, city, zip, additionalAddress } = location || {};
  const {
    carConfiguratorId,
    handleConfiguratorDealer,
    hasCarConfiguratorDealer,
    isConfigurableCar,
  } = carConfigurator || {};
  const contactPersonDataForm = useMemo(
    () => ({
      firstName: contactPerson?.firstName || '',
      lastName: contactPerson?.lastName || '',
      phone: contactPerson?.phone || '',
    }),
    [contactPerson]
  );

  const style = {
    textLinkContainerContact: clsx(
      'sm:col-end-8 sm:col-start-2 sm:flex sm:items-center sm:justify-center sm:mb-[8px] sm:mr-[9%] sm:p-0 sm:w-[100%] md:justify-start md:mr-0 md:pt-25px md:pt-[8px] md:w-full'
    ),
    textLinkContainerLocation: clsx(
      'accessory-dealer-wrapper sm:col-end-24 sm:col-start-18 sm:flex sm:items-center sm:justify-center sm:p-0 sm:w-[100%] md:justify-start md:pt-[8px] md:w-full'
    ),
  };

  const intl = useIntl();
  const [loading, setLoading] = useState<boolean>(false);

  //TODO: open fold having dealerId and the type of clicked link
  // const onClickContact = (dID: string, type: string) => {
  // };

  const isWinterInstance =
    whiteLabel?.merchantOrigin === WL_INTEGRATION_WINTER_LAUSITZ;

  const selectDealerTextLinkText = useMemo(() => {
    if (!hasCarConfiguratorDealer) {
      return translate(intl, 'ui.PDP.DEALER_DETAILS.SELECT_DEALER');
    }
    if (hasCarConfiguratorDealer) {
      return translate(intl, 'ui.PDP.DEALER_DETAILS.SELECT_ANOTHER_DEALER');
    }
    return '';
  }, [hasCarConfiguratorDealer, intl]);

  const router = useRouter();
  const handleBackToSearch = () => {
    dispatch(closeOverlay());
    return router.back();
  };
  const loggedIn = !!useToken();

  const contactProps = {
    children: (
      <ContactDealerForm
        dealerId={id}
        vehicleSku={vehicleSku}
        darkBg={false}
        carModel={carModel}
        contactPerson={contactPersonDataForm}
        carManufacturer={carManufacturer}
        dealerName={name ?? ''}
        onBackToSearchClick={handleBackToSearch}
        customerDetails={customerDetails}
        paymentType={paymentType}
        financeCalculationResult={{
          monthlyInstallment,
          downPayment,
          duration,
          mileage,
        }}
        vehiclePrice={vehiclePrice}
      />
    ),
    title: translate(intl, 'ui.FORMS.CONTACT_DEALER.TITLE'),
  };

  const testDriveProps = {
    children: (
      <TestDriveBookingForm
        dealerId={id}
        vehicleSku={vehicleSku}
        carModel={carModel}
        carManufacturer={carManufacturer}
        dealerName={name ?? ''}
        contactPerson={contactPersonDataForm}
        onBackToSearchClick={handleBackToSearch}
        paymentType={paymentType}
        financeCalculationResult={financeCalculationResult}
        customerDetails={customerDetails}
      />
    ),
    title: translate(intl, 'ui.FORMS.TEST_DRIVE_BOOKING.TITLE'),
  };

  return (
    <div className="flex flex-1 flex-col mt-auto order-2 w-full sm:col-end-24 sm:col-start-2 sm:pb-0 md:col-end-24 md:col-start-19 md:order-none md:pb-0 md:translate-y-[-34px] lg:translate-y-[-30px]">
      {loading && <LoaderOverlay hasZeroOpacity hasLoader={false} />}
      <p
        className="font-weight-bold text-p1 text-purple80 sm:pb-5px sm:pt-20px md:pb-10px md:pt-0 monochrome:text-black"
        id="label--dealer-name"
      >
        {!isWinterInstance
          ? name && !isSpecialCharacter(name) && name
          : 'Winter Automobilpartner'}
      </p>
      <div className="sm:pb-20px md:pb-0">
        {!isWinterInstance && (
          <>
            <p id="label--dealer-address">
              {street && !isSpecialCharacter(street) && street}{' '}
              {houseNumber && !isSpecialCharacter(houseNumber) && houseNumber}
            </p>
            <p>
              {additionalAddress &&
                !isSpecialCharacter(additionalAddress) &&
                additionalAddress}
            </p>
            <p>
              {zip && !isSpecialCharacter(zip) && zip}{' '}
              {city && !isSpecialCharacter(city) && city}
            </p>
          </>
        )}
      </div>
      <div className="sm:pb-25px md:flex-col md:pb-0" data-hide-for-print="">
        {hasCarConfiguratorDealer && !carConfiguratorId && (
          <div className={style.textLinkContainerContact}>
            <TextLink
              text={translate(intl, 'ui.PDP.DEALER_DETAILS.CONTACT')}
              onClick={() => {
                dispatch(openOverlay(contactProps));
              }}
              icon={<Chat16 />}
              id="link--contact"
              mobileBlockVariant={true}
              customClassName="text-p3 md:text-p1"
            />
          </div>
        )}

        {!isAccessory && hasCarConfiguratorDealer && (
          <div
            className={
              'sm:col-end-16 sm:col-start-10 sm:flex sm:items-center sm:justify-center sm:mr-[9%] sm:p-0 sm:w-[100%] md:justify-start md:mr-0 md:w-full sm:mb-[8px] lg:pt-[8px]'
            }
          >
            <TextLink
              text={translate(intl, 'ui.PDP.DEALER_DETAILS.TEST_DRIVE')}
              onClick={() => {
                // dataLayer push
                pushAnalyticsEvent({
                  event: 'test_drive',
                  form_process: 'testdrive_formStart',
                  product_name: `${carManufacturer} ${carModel}`,
                  product_id: vehicleSku,
                });

                dispatch(openOverlay(testDriveProps));
              }}
              icon={<Car16 />}
              id="link--test-drive"
              mobileBlockVariant={true}
              customClassName="text-p3 md:text-p1"
            />
          </div>
        )}

        {!isAccessory && !isOrderVehicle && !isConfigurableCar && (
          <div className="sm:col-end-24 sm:col-start-18 sm:flex sm:items-center sm:justify-center sm:mb-[8px] sm:p-0 sm:w-[100%] md:justify-start md:w-full lg:pt-[8px]">
            <TextLink
              text={translate(intl, 'ui.PDP.DEALER_DETAILS.TRADE_IN')}
              icon={<ArrowsVertical16 />}
              isLoading={loading}
              onClick={async () => {
                setLoading(true);
                await router.push(
                  loggedIn
                    ? `${Routes.TRADE_IN}?${TradeInFlowType.PDP}=${encodeURI(
                        `${carManufacturer}/${carModel}/${vehicleId}`
                      )}`
                    : `${Routes.ACCOUNT.LOGIN}?redirectTo=${encodeURIComponent(
                        `${Routes.TRADE_IN}?${TradeInFlowType.PDP}=${carManufacturer}/${carModel}/${vehicleId}`
                      )}`
                );
                setLoading(false);
              }}
              id="link--test-trade-in"
              mobileBlockVariant={true}
              customClassName="text-p3 md:text-p1"
            />
          </div>
        )}
        {!isAccessory && (
          <div className="mt-[16px]">
            <TextLink
              target="_blank"
              text={translate(intl, 'ui.PDP.PRINT_OFFER')}
              icon={<Printer16 />}
              onClick={() => window.print()}
            />
          </div>
        )}

        {carConfiguratorId && (
          <div className="sm:col-end-24 sm:col-start-18 sm:flex sm:items-center sm:justify-center sm:mb-[8px] sm:p-0 sm:text-center sm:w-[100%] md:justify-start md:pt-[8px] md:text-left md:w-full">
            <TextLink
              text={selectDealerTextLinkText}
              onClick={() =>
                handleConfiguratorDealer && handleConfiguratorDealer()
              }
              icon={<Location16 />}
              id="link--test-accessory"
              mobileBlockVariant={true}
              customClassName="text-p3 md:text-p1"
            />
          </div>
        )}

        {isConfigurableCar && carConfiguratorId && (
          <div className="sm:col-end-24 sm:col-start-18 sm:flex sm:items-center sm:justify-center sm:p-0 sm:text-center sm:w-[100%] md:justify-start md:pt-[8px] md:text-left md:w-full">
            <TextLink
              text={translate(
                intl,
                'ui.PDP.CAR_CONFIGURATOR.RESTART_CONFIGURATION'
              )}
              onClick={openConfigurator}
              icon={<Pen16 />}
              id="link--new-configuration"
              mobileBlockVariant={true}
              customClassName="text-p3 md:text-p1"
            />
          </div>
        )}

        {isAccessory && (
          <div className={style.textLinkContainerLocation}>
            <TextLink
              text={translate(
                intl,
                'ui.PDP.DEALER_DETAILS.SELECT_ANOTHER_DEALER'
              )}
              onClick={onSelectAnotherDealerClick}
              icon={<Location16 />}
              id="link--test-accessory"
              mobileBlockVariant={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CarDealerDetails;
